import { createAsyncThunk, createEntityAdapter, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit"
import { AppDataState, AppThunkAPIType, unknownError } from "../../../app/appTypes"
import { AppDispatch, RootState, store } from "../../../app/store"
import { getWithAuth, postWithAuth } from "../../../http"

export type Op = 'ADD' | 'DELETE' | 'UPDATE'

export interface DepartmentAssignement {
    id:  number
    documentId:  number
    blockKey:  string
    importedDocumentElementId:  number | null
    companyDepartmentId:  number
    companyDepartmentCode:  string
    companyDepartmentName:  string
    isCompleted:  boolean
    completedAt:  string
    completedBy:  string
}

export interface DepartmentAssignementOp {
    operation: Op
    id: number
    blockKey: string
    companyDepartmentId: number
}

export const loadAllDepartmentAssignements = createAsyncThunk<
    DepartmentAssignement[],
    { docId: number },
    AppThunkAPIType
>("imported-documents/department-assignments/load-all",
    async ({ docId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/imported-documents/${docId}/departments-assigments` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as DepartmentAssignement[];
        }

        return rejectWithValue(payload ?? { kind: "unknown" });
    }
);

export const updateDepartmentAssignements = createAsyncThunk<
    DepartmentAssignement[],
   { docId: number, items: DepartmentAssignementOp[] },
    AppThunkAPIType
>("imported-documents/department-assignments/batch-update", async ({ docId, items }, { dispatch, rejectWithValue }) => {
    const result = await dispatch(
        postWithAuth({
            url: `api/imported-documents/${docId}/departments-assigments/batch`,
            payload: {
                items: items,
            },
        })
    );

    const { payload } = result;

    if (postWithAuth.fulfilled.match(result)) {
        return payload as DepartmentAssignement[];
    }

    return rejectWithValue(payload ?? { kind: "unknown" });
});

const adapter = createEntityAdapter<DepartmentAssignement>({
    selectId: a => a.blockKey,
})

interface ImportedDocDepartmentsState {
    docId: number | null
    assignments: EntityState<DepartmentAssignement>
    loaded: AppDataState
    pendingChanges: Record<string, number | null>
}

const initialState: ImportedDocDepartmentsState = {
    docId: null,
    assignments: adapter.getInitialState(),
    loaded: { type: 'empty' },
    pendingChanges: {},
}

const importedDocDepartmentsSlice = createSlice({
    name: 'importedDocDepartments',
    initialState,
    reducers: {
        setDepartmentOnBlock(state, action: PayloadAction<{ blockKey: string; departmentId: number | null }>) {
            const { blockKey, departmentId } = action.payload
            state.pendingChanges[blockKey] = departmentId
        },
    },
    extraReducers: builder => {
        builder.addCase(loadAllDepartmentAssignements.pending, (state, action) => {
            state.docId = action.meta.arg.docId
            state.loaded = {
                type: 'loading',
                requestId: action.meta.requestId,
            }
        })
        builder.addCase(loadAllDepartmentAssignements.fulfilled, (state, action) => {
            state.docId = action.meta.arg.docId
            state.loaded = { type: 'loaded' }
            adapter.setAll(state.assignments, action.payload)
        })
        builder.addCase(loadAllDepartmentAssignements.rejected, (state, action) => {
            state.loaded = {
                type: 'error',
                error: action.payload ?? unknownError(),
            }
        })
        builder.addCase(updateDepartmentAssignements.fulfilled, (state, action) => {
            adapter.setAll(state.assignments, action.payload)
            state.pendingChanges = {}
        })
    },
})

export const { setDepartmentOnBlock } = importedDocDepartmentsSlice.actions

export const saveDepartmentAssignments = async function (docId: number, dispatch: AppDispatch) {
    const state = store.getState() 
    const { pendingChanges, assignments } = state.importedDocDepartments
    const ops: DepartmentAssignementOp[] = []

    for (const [blockKey, desiredDeptId] of Object.entries(pendingChanges)) {
        const existing = assignments.entities[blockKey]

        if (desiredDeptId === null) {
            if (existing) {
                ops.push({
                    operation: 'DELETE',
                    id: existing.id,
                    blockKey,
                    companyDepartmentId: existing.companyDepartmentId
                })
            }
        } else if (!existing) {
            ops.push({
                operation: 'ADD',
                id: 0,
                blockKey,
                companyDepartmentId: desiredDeptId,
            })
        } else if (existing.companyDepartmentId !== desiredDeptId) {
            ops.push({
                operation: 'UPDATE',
                id: existing.id,
                blockKey,
                companyDepartmentId: desiredDeptId,
            })
        }
    }

    if (ops.length > 0) {
        await dispatch(updateDepartmentAssignements({ docId, items: ops })).unwrap()
    }
}

const selectState = (state: RootState) => state.importedDocDepartments

export const selectDeptAssignmentsLoaded = (state: RootState) => state.importedDocDepartments.loaded

export const selectHasPendingDeptChanges = (state: RootState) =>
    Object.keys(state.importedDocDepartments.pendingChanges).length > 0

export const selectEffectiveDeptIdForBlock = (state: RootState, blockKey: string): number | null => {
    const { pendingChanges, assignments } = state.importedDocDepartments
    if (blockKey in pendingChanges) {
        return pendingChanges[blockKey]
    }
    const existing = assignments.entities[blockKey]
    return existing?.companyDepartmentId ?? null
}

export const {
    selectAll: selectAllDeptAssignments,
} = adapter.getSelectors((state: RootState) => selectState(state).assignments)

export default importedDocDepartmentsSlice.reducer
